import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hero } from './Hero';
import { DemoVideo } from './DemoVideo';
import { TrustStrip } from './TrustStrip';
import { ProofDifferentiation } from './ProofDifferentiation';
import { WhatYouGet } from './WhatYouGet';
import { HowItWorks } from './HowItWorks';
import { PricingPreview } from './PricingPreview';
import { FAQ } from './FAQ';
import { Footer } from './Footer';
import { PageSeo } from '../seo/PageSeo';
import {
  organizationSchema,
  softwareApplicationSchema,
  faqSchema,
  breadcrumbSchema,
} from '../seo/schemas';
import { faqEntries } from './faqEntries';
import { trackEvent } from '../../utils/analytics';
import './homepage.css';

export const NewHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    trackEvent('page_view_home');
  }, []);

  const handleStartTranslating = (section: string) => {
    trackEvent('cta_click_start_translating', { section });
    navigate('/get-started');
  };

  const handleSeePricing = (section: string) => {
    trackEvent('cta_click_see_pricing', { section });
    navigate('/pricing');
  };

  const scrollToHowItWorks = () => {
    trackEvent('cta_click_see_how', { section: 'hero' });
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  /*
   * Built from the same list the visible FAQ renders, rather than a second
   * hardcoded copy. See faqEntries.ts: the two had drifted to different wording,
   * different question counts, and English on every locale.
   */
  const homepageFaq = faqSchema(faqEntries(t));

  return (
    <>
      <PageSeo
        path="/"
        titleKey="seo.home.title"
        descriptionKey="seo.home.description"
        ogImage="/og/og-home.png"
        keywords={['localization', 'i18n', 'AI translation', 'CLI', 'CI/CD', 'developer tools']}
        jsonLd={[
          organizationSchema(),
          softwareApplicationSchema(),
          homepageFaq,
          breadcrumbSchema([{ name: 'Home', path: '/' }]),
        ]}
      />

      <div className="homepage">
        <Hero
          onStartTranslating={() => handleStartTranslating('hero')}
          onSeeHow={scrollToHowItWorks}
        />
        <DemoVideo />
        <TrustStrip />
        <ProofDifferentiation />
        <WhatYouGet />
        <div id="how-it-works">
          <HowItWorks />
        </div>
        <PricingPreview onViewPlans={() => handleSeePricing('pricing_preview')} />
        <FAQ />
        <Footer />
      </div>
    </>
  );
};
